
export default (https) => ({
  async getList(params = []) {
    const queryString = params
      .filter(param => param.value !== null && param.value !== undefined && param.value !== '')
      .map(param => `${encodeURIComponent(param.key)}=${encodeURIComponent(param.value)}`)
      .join('&');

    const response = await https.get(`/categories?${queryString}`);
    return response.data;
  },

  /**
   * Every category, fetched in small pages instead of one `take_all=true` call.
   *
   * `GET /categories?take_all=true` never reaches the browser: the server
   * answers 200 with correct CORS headers (verified through the HTTP kernel and
   * by curl), but the response is dropped in transit and Chrome reports it as
   * "No 'Access-Control-Allow-Origin' header is present". Measured behaviour:
   *
   *   per_page=10  -> 10/10 requests succeed, all 8 pages
   *   per_page=15  -> pages blocked even after retries
   *   per_page=20&page=2, take_all=true -> 0/10 succeed
   *
   * It is not payload size — /products returns 48KB over the same connection
   * without trouble — and no individual category triggers it. The cause sits in
   * the hosting edge, so this works around it rather than fixing it.
   *
   * Degrades rather than throws: a page that will not load is skipped so the
   * caller still gets the categories that did, instead of an empty filter and
   * an error toast.
   *
   * Returns the same `{ success, message, data }` shape as getList.
   */
  async getAll(perPage = 10) {
    const fetchPage = async (page) => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await this.getList([
            { key: 'per_page', value: perPage },
            { key: 'page', value: page },
          ]);
        } catch (e) {
          if (attempt === 2) {
            // eslint-disable-next-line no-console
            console.warn(`[category.getAll] page ${page} failed after 3 attempts`, e?.message);
            return null;
          }
        }
      }
      return null;
    };

    const first = await fetchPage(1);
    if (!first) {
      return { success: false, message: 'Could not load categories', data: [] };
    }

    const lastPage = first?.meta?.last_page ?? 1;
    const items = [...(first?.data ?? [])];

    for (let page = 2; page <= lastPage; page++) {
      const next = await fetchPage(page);
      if (next?.data) items.push(...next.data);
    }

    return { success: true, message: 'Record Found', data: items };
  },

  async add(payload) {
    // Check if payload is FormData (for file uploads)
    if (payload instanceof FormData) {
      const response = await https.post(`/categories`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await https.post(`/categories`, payload);
      return response.data;
    }
  },

  async update(id, payload) {
    // Check if payload is FormData (for file uploads)
    if (payload instanceof FormData) {
      payload.append('_method', 'PUT');
      const response = await https.post(`/categories/${id}`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await https.put(`/categories/${id}`, payload);
      return response.data;
    }
  },

  async deleteById(id) {
    const response = await https.delete(`/categories/${id}`);
    return response.data;
  },
});

